import { useQuery } from '@tanstack/react-query';
import { useMemo, useState } from 'react';
import { api, getOptionLabel, getOptionIcon } from '../lib/api';
import { fmt } from '../lib/format';

/* ===== Simulasi Result Types ===== */
interface SimCandidate {
  strike_price: number;
  option_type: 'PUT' | 'CALL';
  apr: number;
  days_to_settle: number;
  soft_score: number;
  gate_passed: { macro: boolean; liquidation_zone: boolean; min_distance: boolean };
  reject_reason: string | null;
}

interface SimResult {
  atr_multiple: number;
  threshold: number;
  threshold_put: number;
  total: number;
  passed: number;
  rejected: number;
  passed_list: SimCandidate[];
  rejected_list: SimCandidate[];
}

interface SimResponse {
  symbol: string;
  spot_price: number;
  atr: number;
  snapshot_at: string;
  results: SimResult[];
}

type TabFilter = 'all' | 'put' | 'call';

/* ===== Est. Profit ===== */
/** PUT: invest = strike → profit = strike × APR% × days/365 */
function estProfitPut(strike: number, apr: number, days: number): number | null {
  if (strike <= 0 || apr <= 0 || days <= 0) return null;
  return strike * (apr / 100) * (days / 365);
}

/** CALL: invest = 1 BTC → profit = spot × APR% × days/365 */
function estProfitCall(spot: number, apr: number, days: number): number | null {
  if (spot <= 0 || apr <= 0 || days <= 0) return null;
  return spot * (apr / 100) * (days / 365);
}

/* ===== Gate Chip ===== */
function GateChip({ passed }: { passed: boolean }) {
  return (
    <span style={{
      display: 'inline-flex', padding: '1px 6px', borderRadius: 999, fontSize: 10, fontWeight: 600,
      color: passed ? 'var(--gl-emerald)' : 'var(--gl-rose)',
      background: passed ? 'rgba(52,211,153,0.1)' : 'rgba(251,113,133,0.1)',
    }}>
      {passed ? '✅' : '❌'}
    </span>
  );
}

/* ===== Main Page ===== */
export function AtAnalysisPage() {
  const [selectedMult, setSelectedMult] = useState<number | null>(null);
  const [tabFilter, setTabFilter] = useState<TabFilter>('all');

  const multiples = [1.5, 2.0, 2.5];

  const { data, isLoading, error } = useQuery({
    queryKey: ['simulate', 'BTCUSDT', multiples],
    queryFn: () => api.post<SimResponse>('/api/bot/simulate', { symbol: 'BTCUSDT', multiples }),
    refetchInterval: 120_000,
  });

  // Fetch USDT/IDR rate
  const { data: usdtIdrData } = useQuery({
    queryKey: ['price', 'USDTIDR'],
    queryFn: () => api.get<{ symbol: string; price: number }>(
      '/api/binance/price?symbol=USDTIDR',
    ),
    refetchInterval: 60_000,
  });

  const spot = data?.spot_price ?? 0;
  const atr = data?.atr ?? 0;
  const usdtIdrRate = usdtIdrData?.price ?? 0;

  const showPut = tabFilter === 'all' || tabFilter === 'put';
  const showCall = tabFilter === 'all' || tabFilter === 'call';

  return (
    <div className="gl-page">
      <div style={{ marginBottom: 20 }}>
        <div style={{ fontSize: 20, fontWeight: 600, color: 'var(--gl-text)' }}>ATR Simulation</div>
        <div style={{ fontSize: 12, color: 'var(--gl-text-muted)', marginTop: 4 }}>
          Simulasi penuh: Gate 1 (macro) + Gate 2 (likuidasi) + Gate 3 (ATR custom) + soft score —{' '}
          <strong>tidak mengubah pengaturan bot (default 2.5×)</strong>
        </div>
      </div>

      {isLoading && <div className="gl-empty">Menjalankan simulasi…</div>}
      {error && <div className="settings-alert settings-alert--error">{(error as Error).message}</div>}

      {data && (
        <>
          {/* ===== Market Ringkasan ===== */}
          <div style={{ display: 'flex', gap: 14, marginBottom: 16, flexWrap: 'wrap' }}>
            <div className="g-card" style={{ padding: '10px 16px', textAlign: 'center', minWidth: 100 }}>
              <div style={{ fontSize: 10, color: 'var(--gl-text-faint)', textTransform: 'uppercase' }}>Spot</div>
              <div style={{ fontSize: 20, fontWeight: 700 }}>{fmt.number(spot, 0)}</div>
            </div>
            <div className="g-card" style={{ padding: '10px 16px', textAlign: 'center', minWidth: 100 }}>
              <div style={{ fontSize: 10, color: 'var(--gl-text-faint)', textTransform: 'uppercase' }}>ATR(14)</div>
              <div style={{ fontSize: 20, fontWeight: 700 }}>{fmt.number(atr, 0)}</div>
              <div style={{ fontSize: 10, color: 'var(--gl-text-muted)' }}>
                {(atr / spot * 100).toFixed(1)}%
              </div>
            </div>
            <div className="g-card" style={{ padding: '10px 16px', textAlign: 'center', minWidth: 100 }}>
              <div style={{ fontSize: 10, color: 'var(--gl-text-faint)', textTransform: 'uppercase' }}>Bot default</div>
              <div style={{ fontSize: 20, fontWeight: 700, color: 'var(--gl-text-faint)' }}>2.5×</div>
              <div style={{ fontSize: 10, color: 'var(--gl-text-muted)' }}>
                min {fmt.number(spot + atr * 2.5, 0)}
              </div>
            </div>
            {usdtIdrRate > 0 && (
              <div className="g-card" style={{ padding: '10px 16px', textAlign: 'center', minWidth: 100 }}>
                <div style={{ fontSize: 10, color: 'var(--gl-text-faint)', textTransform: 'uppercase' }}>USDT/IDR</div>
                <div style={{ fontSize: 20, fontWeight: 700, color: 'var(--gl-amber)' }}>
                  Rp {fmt.number(usdtIdrRate, 0)}
                </div>
              </div>
            )}
          </div>

          {/* ===== Perbandingan Multiple ===== */}
          <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(200px, 1fr))', gap: 14, marginBottom: 16 }}>
            {data.results.map((r) => {
              const callsPassed = r.passed_list.filter((c) => c.option_type === 'CALL');
              const putsPassed = r.passed_list.filter((c) => c.option_type === 'PUT');
              return (
                <div
                  key={r.atr_multiple}
                  className="g-card"
                  style={{
                    padding: 16, cursor: 'pointer',
                    border: selectedMult === r.atr_multiple ? '1px solid var(--gl-emerald)' : 'none',
                  }}
                  onClick={() => setSelectedMult(selectedMult === r.atr_multiple ? null : r.atr_multiple)}
                >
                  <div style={{ fontSize: 22, fontWeight: 700, marginBottom: 4 }}>
                    {r.atr_multiple}×
                  </div>
                  <div style={{ fontSize: 11, color: 'var(--gl-text-faint)', marginBottom: 8 }}>
                    Gate 3: {fmt.number(r.threshold_put, 0)} — {fmt.number(r.threshold, 0)}
                  </div>
                  <div style={{ display: 'flex', gap: 16, fontSize: 13 }}>
                    <div>
                      <div style={{ fontSize: 10, color: 'var(--gl-text-faint)', textTransform: 'uppercase' }}>Total</div>
                      <strong>{r.total}</strong>
                    </div>
                    <div>
                      <div style={{ fontSize: 10, color: 'var(--gl-text-faint)', textTransform: 'uppercase' }}>Lolos</div>
                      <strong style={{ color: 'var(--gl-emerald)' }}>{r.passed}</strong>
                    </div>
                    <div>
                      <div style={{ fontSize: 10, color: 'var(--gl-text-faint)', textTransform: 'uppercase' }}>Ditolak</div>
                      <strong style={{ color: 'var(--gl-rose)' }}>{r.rejected}</strong>
                    </div>
                  </div>
                  {r.passed > 0 && (
                    <div style={{ marginTop: 8, fontSize: 11 }}>
                      <div>↑ CALL: <strong>{callsPassed.length}</strong> lolos</div>
                      <div>↓ PUT: <strong>{putsPassed.length}</strong> lolos</div>
                    </div>
                  )}
                </div>
              );
            })}
          </div>

          {/* ===== Detail Rekomendasi per Multiple ===== */}
          {selectedMult && data.results
            .filter((r) => r.atr_multiple === selectedMult)
            .map((r) => {
              const puts = r.passed_list
                .filter((c) => c.option_type === 'PUT')
                .sort((a, b) => a.days_to_settle - b.days_to_settle);
              const calls = r.passed_list
                .filter((c) => c.option_type === 'CALL')
                .sort((a, b) => a.days_to_settle - b.days_to_settle);

              return (
                <div key={r.atr_multiple}>
                  {/* Filter chips */}
                  <div className="g-card rvl" style={{ padding: 12, marginBottom: 12, display: 'flex', gap: 8, alignItems: 'center' }}>
                    <span className="gl-label" style={{ marginBottom: 0, marginRight: 8 }}>
                      {r.atr_multiple}× — {r.passed} produk lolos
                    </span>
                    {([
                      { key: 'all' as TabFilter, label: 'Semua' },
                      { key: 'put' as TabFilter, label: 'Buy Low' },
                      { key: 'call' as TabFilter, label: 'Sell High' },
                    ]).map((f) => (
                      <button
                        key={f.key}
                        className={`dcd-explorer__filter ${tabFilter === f.key ? 'dcd-explorer__filter--active' : ''}`}
                        onClick={() => setTabFilter(f.key)}
                      >
                        {f.key === 'put' && <span style={{ color: 'var(--gl-cyan)', marginRight: 4 }}>●</span>}
                        {f.key === 'call' && <span style={{ color: 'var(--gl-amber)', marginRight: 4 }}>●</span>}
                        {f.label}
                      </button>
                    ))}
                  </div>

                  {r.passed_list.length === 0 ? (
                    <div className="g-card" style={{ padding: 16, marginBottom: 16 }}>
                      <div className="gl-empty">Tidak ada yang lolos Gate 3 + macro + likuidasi + score.</div>
                    </div>
                  ) : (
                    <>
                      {/* ===== PUT (Buy Low) ===== */}
                      {showPut && (
                        <div className="g-card rvl" style={{ marginBottom: 16 }}>
                          <div style={{ display: 'flex', alignItems: 'center', gap: 10, marginBottom: 12 }}>
                            <span className="gl-badge gl-badge--info">PUT</span>
                            <span className="gl-label" style={{ marginBottom: 0 }}>Buy Low — Beli BTC di harga strike</span>
                            <span style={{ marginLeft: 'auto', fontSize: 12, color: 'var(--gl-text-faint)' }}>
                              {puts.length} produk · settlement terdekat di atas
                            </span>
                          </div>
                          <div style={{ fontSize: 11, color: 'var(--gl-text-faint)', marginBottom: 16 }}>
                            Invest <b style={{ color: 'var(--gl-text)' }}>USDT = harga strike</b> → jika BTC ≤ strike saat settlement, kamu dapat BTC
                          </div>
                          {puts.length === 0 ? (
                            <div className="gl-empty" style={{ marginBottom: 0 }}>Tidak ada produk PUT yang lolos</div>
                          ) : (
                            <table className="glt">
                              <thead>
                                <tr>
                                  <th>Sisa Hari</th>
                                  <th>Beli BTC di</th>
                                  <th>APR</th>
                                  <th>Gate 🟢🔴</th>
                                  <th>Score</th>
                                  <th>Est. Profit (USDT)</th>
                                  {usdtIdrRate > 0 && <th>Est. Profit (IDR)</th>}
                                </tr>
                              </thead>
                              <tbody>
                                {puts.map((c, i) => {
                                  const profitUsdt = estProfitPut(c.strike_price, c.apr, c.days_to_settle);
                                  const profitIdr = profitUsdt !== null && usdtIdrRate > 0 ? profitUsdt * usdtIdrRate : null;
                                  return (
                                    <tr key={i}>
                                      <td>
                                        <span style={{ fontWeight: 600, color: c.days_to_settle <= 2 ? 'var(--gl-rose)' : 'var(--gl-text)' }}>
                                          {c.days_to_settle}
                                        </span>
                                        <span style={{ fontSize: 11, color: 'var(--gl-text-faint)', marginLeft: 2 }}>hari</span>
                                      </td>
                                      <td style={{ fontFamily: 'var(--font-mono)', fontSize: 13, fontWeight: 600 }}>
                                        {fmt.usd(c.strike_price, 2)}
                                      </td>
                                      <td style={{ color: 'var(--gl-emerald)', fontWeight: 600 }}>
                                        {fmt.percent(c.apr, 2)}
                                      </td>
                                      <td>
                                        <div style={{ display: 'flex', gap: 3, alignItems: 'center' }}>
                                          <GateChip passed={c.gate_passed.macro} />
                                          <GateChip passed={c.gate_passed.liquidation_zone} />
                                          <GateChip passed={c.gate_passed.min_distance} />
                                        </div>
                                      </td>
                                      <td>
                                        <span style={{
                                          display: 'inline-flex', padding: '2px 8px', borderRadius: 999,
                                          fontSize: 11, fontWeight: 600,
                                          background: c.soft_score < 30 ? 'rgba(52,211,153,0.1)' : c.soft_score < 60 ? 'rgba(251,191,36,0.1)' : 'rgba(251,113,133,0.1)',
                                          color: c.soft_score < 30 ? 'var(--gl-emerald)' : c.soft_score < 60 ? 'var(--gl-amber)' : 'var(--gl-rose)',
                                        }}>
                                          {fmt.score(c.soft_score)}
                                        </span>
                                      </td>
                                      <td style={{ color: 'var(--gl-emerald)', fontWeight: 600, fontFamily: 'var(--font-mono)' }}>
                                        {profitUsdt !== null ? fmt.usd(profitUsdt, 2) : '—'}
                                      </td>
                                      {usdtIdrRate > 0 && (
                                        <td style={{ color: 'var(--gl-amber)', fontFamily: 'var(--font-mono)', fontSize: 12 }}>
                                          {profitIdr !== null ? `Rp ${fmt.number(profitIdr, 0)}` : '—'}
                                        </td>
                                      )}
                                    </tr>
                                  );
                                })}
                              </tbody>
                            </table>
                          )}
                        </div>
                      )}

                      {/* ===== CALL (Sell High) ===== */}
                      {showCall && (
                        <div className="g-card rvl" style={{ marginBottom: 16 }}>
                          <div style={{ display: 'flex', alignItems: 'center', gap: 10, marginBottom: 12 }}>
                            <span className="gl-badge gl-badge--warn">CALL</span>
                            <span className="gl-label" style={{ marginBottom: 0 }}>Sell High — Jual BTC di harga strike</span>
                            <span style={{ marginLeft: 'auto', fontSize: 12, color: 'var(--gl-text-faint)' }}>
                              {calls.length} produk · settlement terdekat di atas
                            </span>
                          </div>
                          <div style={{ fontSize: 11, color: 'var(--gl-text-faint)', marginBottom: 16 }}>
                            Invest <b style={{ color: 'var(--gl-text)' }}>1 BTC</b> → jika BTC ≥ strike saat settlement, BTC kamu dijual di harga strike
                          </div>
                          {calls.length === 0 ? (
                            <div className="gl-empty" style={{ marginBottom: 0 }}>Tidak ada produk CALL yang lolos</div>
                          ) : (
                            <table className="glt">
                              <thead>
                                <tr>
                                  <th>Sisa Hari</th>
                                  <th>Jual BTC di</th>
                                  <th>APR</th>
                                  <th>Gate 🟢🔴</th>
                                  <th>Score</th>
                                  <th>Est. Profit (USDT)</th>
                                  {usdtIdrRate > 0 && <th>Est. Profit (IDR)</th>}
                                </tr>
                              </thead>
                              <tbody>
                                {calls.map((c, i) => {
                                  const profitUsdt = estProfitCall(spot, c.apr, c.days_to_settle);
                                  const profitIdr = profitUsdt !== null && usdtIdrRate > 0 ? profitUsdt * usdtIdrRate : null;
                                  return (
                                    <tr key={i}>
                                      <td>
                                        <span style={{ fontWeight: 600, color: c.days_to_settle <= 2 ? 'var(--gl-rose)' : 'var(--gl-text)' }}>
                                          {c.days_to_settle}
                                        </span>
                                        <span style={{ fontSize: 11, color: 'var(--gl-text-faint)', marginLeft: 2 }}>hari</span>
                                      </td>
                                      <td style={{ fontFamily: 'var(--font-mono)', fontSize: 13, fontWeight: 600 }}>
                                        {fmt.usd(c.strike_price, 2)}
                                      </td>
                                      <td style={{ color: 'var(--gl-emerald)', fontWeight: 600 }}>
                                        {fmt.percent(c.apr, 2)}
                                      </td>
                                      <td>
                                        <div style={{ display: 'flex', gap: 3, alignItems: 'center' }}>
                                          <GateChip passed={c.gate_passed.macro} />
                                          <GateChip passed={c.gate_passed.liquidation_zone} />
                                          <GateChip passed={c.gate_passed.min_distance} />
                                        </div>
                                      </td>
                                      <td>
                                        <span style={{
                                          display: 'inline-flex', padding: '2px 8px', borderRadius: 999,
                                          fontSize: 11, fontWeight: 600,
                                          background: c.soft_score < 30 ? 'rgba(52,211,153,0.1)' : c.soft_score < 60 ? 'rgba(251,191,36,0.1)' : 'rgba(251,113,133,0.1)',
                                          color: c.soft_score < 30 ? 'var(--gl-emerald)' : c.soft_score < 60 ? 'var(--gl-amber)' : 'var(--gl-rose)',
                                        }}>
                                          {fmt.score(c.soft_score)}
                                        </span>
                                      </td>
                                      <td style={{ color: 'var(--gl-emerald)', fontWeight: 600, fontFamily: 'var(--font-mono)' }}>
                                        {profitUsdt !== null ? fmt.usd(profitUsdt, 2) : '—'}
                                      </td>
                                      {usdtIdrRate > 0 && (
                                        <td style={{ color: 'var(--gl-amber)', fontFamily: 'var(--font-mono)', fontSize: 12 }}>
                                          {profitIdr !== null ? `Rp ${fmt.number(profitIdr, 0)}` : '—'}
                                        </td>
                                      )}
                                    </tr>
                                  );
                                })}
                              </tbody>
                            </table>
                          )}
                        </div>
                      )}
                    </>
                  )}

                  {/* ===== Gate 3 threshold ===== */}
                  <div className="g-card rvl" style={{ padding: 14, marginBottom: 16 }}>
                    <div style={{ fontSize: 13, fontWeight: 600, marginBottom: 10 }}>📏 Gate 3 — Min Distance ({r.atr_multiple}× ATR)</div>
                    <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 14, fontSize: 12 }}>
                      <div>
                        <div style={{ color: 'var(--gl-text-faint)' }}>↓ PUT (Buy Low USDT)</div>
                        <div style={{ fontSize: 18, fontWeight: 700 }}>
                          ≤ {fmt.number(r.threshold_put, 0)}
                        </div>
                        <div style={{ color: 'var(--gl-text-muted)' }}>
                          Jarak: {fmt.number(spot - r.threshold_put, 0)} ({((spot - r.threshold_put) / spot * 100).toFixed(1)}%)
                        </div>
                      </div>
                      <div>
                        <div style={{ color: 'var(--gl-text-faint)' }}>↑ CALL (Sell High BTC)</div>
                        <div style={{ fontSize: 18, fontWeight: 700 }}>
                          ≥ {fmt.number(r.threshold, 0)}
                        </div>
                        <div style={{ color: 'var(--gl-text-muted)' }}>
                          Jarak: {fmt.number(r.threshold - spot, 0)} ({((r.threshold - spot) / spot * 100).toFixed(1)}%)
                        </div>
                      </div>
                    </div>
                  </div>
                </div>
              );
            })}
        </>
      )}

      {/* ===== Penjelasan ===== */}
      <div style={{ fontSize: 11, color: 'var(--gl-text-faint)', lineHeight: 1.5, marginTop: 8 }}>
        <strong>⚠️ Ini simulasi saja.</strong> Data dari Binance langsung. Hasil tidak disimpan ke DB dan tidak mempengaruhi bot.
        Bot default tetap berjalan dengan <strong>2.5× ATR</strong> di Settings. Simulasi ini hanya untuk referensi sebelum Anda
        mengubah ATR multiple di panel Settings. Est. Profit: PUT = strike × APR × hari / 365 | CALL = spot × APR × hari / 365
      </div>
    </div>
  );
}
